import React, { useMemo, memo } from 'react';
import classNames from 'classnames';
import { Table } from 'react-bootstrap';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import Tooltip from '../../../../../../components/Tooltip';

import { getProvider, getProviderName, getBoolRoundingRate, getRoundingRate, isDangerRate } from '../../../../Main/helpers/getters';

const ServiceItem = ({ service, config, serviceForm, onChangeRate, onChangePercent, showProviderColumn, index, style }) => {
  const provider = useMemo(() => getProvider(service.provider_id), [service.provider_id]);
  const providerName = useMemo(() => getProviderName(service.provider_id, service.mode), [service.provider_id, service.mode]);

  const mode = Number(service.mode);
  const hideProvider = config?.access?.hide_provider;
  const isCanEditMassRates = service?.can_edit_mass_rates;
  const showProviderServiceId = service?.provider_show_provider_service_id;
  const isProviderError = service?.provider_error;
  const isRowDisabled = !isCanEditMassRates || isProviderError || !service.visibility;

  const isSameProviderCurrency = mode && provider && config.currency === service?.provider_currency;
  const isProviderCurrency = !!service?.provider_currency;
  const isRateCustom = !!(service.rate && service.rate.custom);
  const isRateProvider = !!(service.rate && service.rate.provider);
  const isRateConverted = !!(service.rate && service.rate.converted);
  const isRateDanger = isDangerRate(service.rate);

  const onBlurRate = (e) => {
    const newTarget = e;
    if (e.target.value) {
      newTarget.target.value = parseFloat(e.target.value);
      if (serviceForm.rate.custom_float !== newTarget.target.value) {
        onChangeRate(newTarget, service.id);
      }
    }
  };

  const tooltipBody = () => {
    if (!service.visibility) {
      return (
        <p>
          The service can not be updated
          <br />
          {' '}
          because the service is disabled
        </p>
      );
    }

    if (isProviderError) {
      return (
        <p>
          The service can not be updated because
          <br />
          {' '}
          the service is disabled by provider
        </p>
      );
    }

    if (!isCanEditMassRates) {
      return (
        <p>
          The service can not be updated because the system
          <br />
          {' '}
          hasn't processed the previous price change yet
        </p>
      );
    }
  };

  return (
    <div
      key={index}
      style={style}
      className={classNames('list-item table-service', {
        'table-service__disabled': isRowDisabled,
      })}
    >
      <Table>
        <tbody>
          <tr style={{ height: style.height }}>
            <td className="service-name">
              <div className="d-flex align-items-center">
                <div className="sid">{service.id}</div>
                <div className="sname">{service.name}</div>
              </div>
            </td>
            {showProviderColumn ? (
              <td className="service-provider">
                {providerName === 'error_provider' && !hideProvider ? (
                  <>Manual</>
                ) : (
                  <>{hideProvider && mode ? null : <div className="service-provider__name">{providerName}</div>}</>
                )}
                {service.provider_service_id && !hideProvider && showProviderServiceId ? (
                  <div className="service-provider__id">
                    <span>{service.provider_service_id}</span>
                  </div>
                ) : null}
              </td>
            ) : null}
            <td className="service-fixed">{service?.auto_rate_fixed !== '0.00' ? service?.auto_rate_fixed : null}</td>
            <td className="service-percent">
              {service?.provider_type === 1 && service?.auto_rate ? (
                <div className="percent-input">
                  <label className="percent-input__label">
                    <input
                      className="form-control"
                      disabled={isRowDisabled}
                      onChange={(e) => onChangePercent(e, service.id)}
                      value={serviceForm?.auto_rate_percent || '0'}
                    />
                  </label>
                </div>
              ) : null}
            </td>
            <td className="service-rate">
              {isRateCustom ? (
                <div
                  className={classNames('service-rate__rate-custom', {
                    'service-rate__sync-wrapper': service.auto_rate,
                    'service-rate__rate-danger': isRateDanger,
                  })}
                >
                  {service.auto_rate ? (
                    <Tooltip placement="top" tooltipClass="tooltip__top" text="Rate is synced with provider">
                      <div className="service-rate__sync-icon" />
                    </Tooltip>
                  ) : null}
                  <div>
                    {getBoolRoundingRate(service.rate.custom) ? (
                      <span className="service-rate__rate-rounding">
                        <Tooltip placement="left" tooltipClass="tooltip__left" text={service.rate.custom}>
                          <span>{getRoundingRate(service.rate.custom)}</span>
                        </Tooltip>
                      </span>
                    ) : (
                      service.rate.custom_float
                    )}
                  </div>
                </div>
              ) : null}
              {isRateProvider || isRateConverted ? (
                <div className="service-rate__rate-provider">
                  {isRateConverted && !isSameProviderCurrency ? (
                    <Tooltip
                      placement="left"
                      tooltipClass="tooltip__left"
                      text={`${Number(service.rate.provider)} ${isProviderCurrency ? provider.currency : ''}`}
                    >
                      <span>
                        ≈
                        {' '}
                        {service.rate.converted}
                      </span>
                    </Tooltip>
                  ) : (
                    <span>{isRateProvider ? Number(service.rate.provider) : null}</span>
                  )}
                  {isProviderCurrency && !isRateConverted && !isSameProviderCurrency ? (
                    <span className="service-rate__rate-provider-currency">{provider.currency}</span>
                  ) : null}
                </div>
              ) : null}
            </td>
            <td className="service-rate-new">
              <div className="position-relative service-rate__sync">
                {service?.provider_type === 1 && service?.auto_rate ? (
                  <>
                    {service.auto_rate || !isEqual(service.rate.custom_float, serviceForm?.rate?.custom_float) ? (
                      <Tooltip placement="top" tooltipClass="tooltip__top" text="Rate is synced with provider">
                        <div className="service-rate__sync-icon" />
                      </Tooltip>
                    ) : null}
                    <span>{serviceForm?.rate?.custom_float}</span>
                  </>
                ) : (
                  <input
                    className="form-control"
                    disabled={isRowDisabled}
                    onChange={(e) => onChangeRate(e, service.id)}
                    onBlur={onBlurRate}
                    value={serviceForm?.rate?.custom_float || ''}
                  />
                )}
                {isRowDisabled ? (
                  <Tooltip placement="left" tooltipClass="tooltip__left tw-320 ttl" text={tooltipBody()}>
                    <span className="fas fa-exclamation-circle cursor-help" />
                  </Tooltip>
                ) : null}
              </div>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

ServiceItem.propTypes = {
  service: PropTypes.object,
  config: PropTypes.object,
  serviceForm: PropTypes.object,
  onChangeRate: PropTypes.func,
  onChangePercent: PropTypes.func,
  showProviderColumn: PropTypes.bool,
  index: PropTypes.number,
  style: PropTypes.object,
};

const arePropsEqual = () => false;
export default memo(ServiceItem, arePropsEqual);
